import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BottomFields,
  EventsMatchesWrap,
  FieldCount,
  FootballField,
  FootballFieldCount,
  LeftTopField,
  ListOfMatches,
  MiddleTopField,
  RightTopField,
  TopFields,
} from "../../../style/EventsMatchesStyle";
import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  MatchDropdownItem,
  MatchCheckbox,
  EventCard,
  EventCardWrapper,
  EventContent,
  EventTabsCard,
  EventTabsHeader,
  EventTabsScrollArea,
  FootballFielOptions,
  ParametersTabWrapper,
  ParameterSearchInput,
} from "../shared/ui/EventSharedStyles";
import VideoSettingsComponent from "../../../components/Events/VideoSettings/VideoSettings";
import DateRangeSlider from "../../../components/Events/ChooseSeasonSelector/DateRangeSlider";
import {
  TabContent,
  TabListWrapper,
  TabsWrapper,
  TabTitle,
  TopList,
} from "../../../style/Favorites";
import Button from "../../../components/button";
import { TimelineChange } from "../../../components/Events/TimelineChange";
import { getCustomZones, getZones } from "./helpers";
import { useLexicon } from "../../../hooks/useLexicon";
import { ChoosenSelectionsPopover } from "../../../components/Events/ChooseSeasonSelector/ChoosenSelectionsPopover";
import { openVideoList } from "../../../helpers/service";
import { formatMatchScore as formatMatchScoreValue } from "../../../helpers/matchScore";
import {
  getMarkerEventColor,
} from "../../../helpers/eventMarkerColors";
import { mirrorCoordinates } from "../../../helpers";
import useOutsideClick from "../../../hooks/useOutsideClick";
import WatchEventsButton from "../shared/ui/WatchEventsButton";
import useActionSelection from "../shared/hooks/useActionSelection";
import useParameterSearch from "../shared/hooks/useParameterSearch";
import useTimelineFilter from "../shared/hooks/useTimelineFilter";
import usePlayerDualSideEventsData from "../shared/hooks/usePlayerDualSideEventsData";
import { buildEventsResultLimitMessage } from "../shared/eventsResultLimit";
import usePitchDrawingZones from "../shared/hooks/usePitchDrawingZones";
import useEventMarkerHover from "../shared/hooks/useEventMarkerHover";
import {
  PitchTrackerFrame,
  PitchTrackerSvg,
} from "../shared/ui/EventPitchTrackerStyles";
import EventPitchBaseLayer from "../shared/ui/EventPitchBaseLayer";
import EventHoverTracker from "../shared/ui/EventHoverTracker";
import PitchLoadingOverlay from "../shared/ui/PitchLoadingOverlay";
import SelectedActionsPopoverContent from "../shared/ui/SelectedActionsPopoverContent";
import EventHalfPitchStaticPreview from "../shared/ui/EventHalfPitchStaticPreview";
import SequenceContextFilterPanel from "../shared/ui/SequenceContextFilterPanel";
import CollapsibleFilterSection, {
  useOverflowScrollIndicator,
} from "../shared/ui/CollapsibleFilterSection";
import PageScrollDownButton from "../shared/ui/PageScrollDownButton";
import EventsTutorial from "../shared/ui/EventsTutorial";
import {
  calculateZonePercentages,
  convertHalfPitchCoordinates,
  countHalfPitchZones,
} from "../shared/utils/pitchMath";
import arrowIconDown from "../../../images/down.svg";
import arrowIconUp from "../../../images/up.svg";
import {
  filterMatchesByDateRange,
  getMatchesSummaryLabel,
} from "./helpers/pageFilters";

export { Dropdown, DropdownButton, DropdownContent, DropdownItem };

const _isLightTheme = localStorage.getItem('theme') === 'light';
const BODY_PART_OPTIONS = [
  { id: 2, label: "Left foot" },
  { id: 1, label: "Right foot" },
  { id: 3, label: "Header" },
  { id: 4, label: "Body" },
];
const BODY_PART_DISABLED_TOOLTIP = "Avaliable only for shots and goals.";
const SHOT_OR_GOAL_ACTION_RE = /\b(shots?|goals?)\b/i;
const ATTACK_FLANK_OPTIONS = [
  { id: "left", label: "Left" },
  { id: "center", label: "Center" },
  { id: "right", label: "Right" },
];
const ENDING_SEQUENCE_CONTEXT_FILTER_IDS = new Set([
  "possession_ending_shot",
  "possession_ending_goal",
  "possession_ending_cross",
  "possession_ending_possession_change",
  "possession_box_entry",
  "possession_final_third_entry",
]);
const PRESSING_SEQUENCE_CONTEXT_FILTER_IDS = new Set([
  "high_pressing",
  "mid_pressing",
  "low_block",
  "counter_pressing",
]);

const EndingCreatorToggle = styled.label`
  align-items: center;
  background: rgba(191, 232, 45, 0.1);
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 8px;
  color: ${(props) => props.theme.blueish2};
  cursor: pointer;
  display: flex;
  font-family: "SatoshiMedium";
  font-size: 13px;
  justify-content: space-between;
  margin: 12px 0 8px;
  padding: 10px 12px;
`;

const EndingCreatorToggleInput = styled.input`
  height: 0;
  opacity: 0;
  position: absolute;
  width: 0;

  &:checked + span {
    background: #bfe82d;
  }

  &:checked + span::after {
    transform: translateX(16px);
  }
`;

const EndingCreatorToggleTrack = styled.span`
  background: ${(props) => props.theme.borderColor};
  border-radius: 12px;
  height: 20px;
  position: relative;
  transition: background 0.2s ease;
  width: 36px;

  &::after {
    background: #fff;
    border-radius: 50%;
    content: "";
    height: 16px;
    left: 2px;
    position: absolute;
    top: 2px;
    transition: transform 0.2s ease;
    width: 16px;
  }
`;

const EventsPlayer = ({
  players,
  paramsList,
  user,
  selectedMatchIds = [],
  selectedMatches = [],
  isActive = false,
}) => {
  const l = useLexicon();
  const { id } = useParams();

  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [showDirection, setShowDirection] = useState(false);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [isParametersOpen, setIsParametersOpen] = useState(false);
  const [typeShots, setTypeShots] = useState("own"); // own | opponent
  const [filteredMatchIds, setFilteredMatchIds] = useState(selectedMatchIds);
  const [openMatchesSelect, setOpenMatchesSelect] = useState(false);
  // Body part filter for markers_search: [] = all, 1 = right foot, 2 = left foot, 3 = header, 4 = body
  const [bodyPartIds, setBodyPartIds] = useState([]);
  const [flankIds, setFlankIds] = useState([]);
  const [sequenceContextFilterIds, setSequenceContextFilterIds] = useState([]);
  const [requirePlayerAsEndingCreator, setRequirePlayerAsEndingCreator] = useState(true);

  const dataForTimeLineChange = useRef(null);
  const userState = useSelector((state) => state.user.userProfile);

  const {
    checkedIds,
    checkedParamIds,
    checkedAction,
    groupedSelectedActions,
    handleCheckboxChange,
    resetActionSelection,
  } = useActionSelection();

  const {
    query: paramSearchQuery,
    handleSearchChange: handleParamSearchChange,
    selectedTabIndex: paramTabIndex,
    setSelectedTabIndex: setParamTabIndex,
    highlightedActionId,
    highlightedRef,
    tabListRef,
    searchInputRef,
  } = useParameterSearch(paramsList, l, checkedIds);
  const {
    scrollRef: parameterScrollRef,
    showIndicator: showParameterScrollIndicator,
  } = useOverflowScrollIndicator(isParametersOpen);

  const toggleBodyPart = (partId) => {
    setBodyPartIds((prev) =>
      prev.includes(partId) ? prev.filter((x) => x !== partId) : [...prev, partId]
    );
  };

  const toggleFlank = useCallback((flankId) => {
    setFlankIds((prev) =>
      prev.includes(flankId)
        ? prev.filter((currentId) => currentId !== flankId)
        : [...prev, flankId]
    );
  }, []);

  const toggleSequenceContextFilter = useCallback((filterId) => {
    setSequenceContextFilterIds((prev) =>
      prev.includes(filterId)
        ? prev.filter((currentId) => currentId !== filterId)
        : [...prev, filterId]
    );
  }, []);

  const clearSequenceContextFilters = useCallback(() => {
    setSequenceContextFilterIds([]);
    setRequirePlayerAsEndingCreator(true);
  }, []);

  const hasEndingSequenceContext = useMemo(
    () => sequenceContextFilterIds.some((filterId) => ENDING_SEQUENCE_CONTEXT_FILTER_IDS.has(filterId)),
    [sequenceContextFilterIds]
  );

  const matchesListDropdownRef = useOutsideClick(() => {
    if (openMatchesSelect) {
      setOpenMatchesSelect(false);
    }
  });

  useEffect(() => {
    setFilteredMatchIds(selectedMatchIds);
  }, [selectedMatchIds]);

  const filteredMatchIdSet = useMemo(
    () => new Set((filteredMatchIds || []).map((matchId) => String(matchId))),
    [filteredMatchIds]
  );

  const selectedFilteredMatches = useMemo(
    () =>
      (selectedMatches || []).filter((match) =>
        filteredMatchIdSet.has(String(match?.id))
      ),
    [filteredMatchIdSet, selectedMatches]
  );

  const formatMatchDate = useCallback((matchDate) => {
    if (!matchDate) {
      return "-";
    }

    const parsedDate = new Date(matchDate);
    if (!Number.isFinite(parsedDate.getTime())) {
      return "-";
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${day}/${month}/${year}`;
  }, []);

  const formatMatchScore = useCallback((match) => {
    return formatMatchScoreValue(match, { separator: " : " });
  }, []);

  const getMatchDisplayLabel = useCallback(
    (match) => {
      const homeTeam = match?.home_team || "-";
      const awayTeam = match?.away_team || "-";
      const score = formatMatchScore(match);
      const competition = match?.competition || "-";
      const formattedDate = formatMatchDate(match?.date);

      return `${homeTeam} ${score} ${awayTeam} | ${competition} | ${formattedDate}`;
    },
    [formatMatchDate, formatMatchScore]
  );

  const selectedMatchesSummaryLabel = useMemo(() => {
    const fallbackSummary = getMatchesSummaryLabel(selectedFilteredMatches, l(12));
    if (selectedFilteredMatches.length === 0) {
      return fallbackSummary;
    }

    const labels = selectedFilteredMatches.map((match) => getMatchDisplayLabel(match));
    if (labels.length > 1) {
      return `${labels[0]} + ${labels.length - 1}`;
    }

    return labels[0] || fallbackSummary;
  }, [getMatchDisplayLabel, l, selectedFilteredMatches]);

  const hasFilteredMatchSelection = filteredMatchIds.length > 0;

  const handleChangeMatches = useCallback((match) => {
    setFilteredMatchIds((prev) => {
      const nextIds = new Set((prev || []).map((matchId) => String(matchId)));
      const currentId = String(match?.id);

      if (nextIds.has(currentId)) {
        nextIds.delete(currentId);
      } else {
        nextIds.add(currentId);
      }

      return (selectedMatches || [])
        .map((item) => item?.id)
        .filter((matchId) => nextIds.has(String(matchId)));
    });
  }, [selectedMatches]);

  const handleDeselectAllMatches = useCallback(() => {
    setFilteredMatchIds([]);
  }, []);

  const handleDateRangeChange = useCallback((startDate, endDate) => {
    const matchesInRange = filterMatchesByDateRange({
      matches: selectedMatches,
      startDate,
      endDate,
    });

    setFilteredMatchIds(matchesInRange.map((match) => match.id));
  }, [selectedMatches]);

  const buildSearchPayload = useCallback(
    ({ markers: markersToSearch }) => {
      const payload = { markers: markersToSearch };

      if (bodyPartIds?.length > 0) {
        payload.body_part_id = bodyPartIds;
      }

      if (flankIds?.length > 0) {
        payload.flank_ids = flankIds;
      }

      if (sequenceContextFilterIds?.length > 0) {
        payload.sequence_context_filter_ids = sequenceContextFilterIds;

        if (sequenceContextFilterIds.some((filterId) => PRESSING_SEQUENCE_CONTEXT_FILTER_IDS.has(filterId))) {
          payload.selected_parameter_ids = checkedParamIds;
        }
      }

      const selectedPlayerId = players?.player?.id ?? id;
      if (hasEndingSequenceContext && selectedPlayerId) {
        payload.player_ending_context_player_id = Number(selectedPlayerId);
        payload.player_ending_context_require_same_creator = requirePlayerAsEndingCreator;
      }

      return payload;
    },
    [
      bodyPartIds,
      checkedParamIds,
      flankIds,
      hasEndingSequenceContext,
      id,
      players?.player?.id,
      requirePlayerAsEndingCreator,
      sequenceContextFilterIds,
    ]
  );

  const {
    response,
    responseOpponents,
    ownMarkerColorMap,
    opponentMarkerColorMap,
    allOwnMarkers,
    allOpponentsMarkers,
    loadingMarkers,
    limitExceededBySide,
    timeLineRange,
    resetData,
  } = usePlayerDualSideEventsData({
    id,
    teamId: players?.player?.team?.id,
    playerId: players?.player?.id ?? id,
    playerName: players?.player?.display_name,
    matchesList: filteredMatchIds,
    checkedIds,
    checkedParamIds,
    checkedAction,
    mergeOffset: userState?.merge_offset,
    buildSearchPayload,
    searchDeps: [bodyPartIds, flankIds, sequenceContextFilterIds, requirePlayerAsEndingCreator],
  });

  const currentDisplayedResponseData = useMemo(() => {
    return typeShots === "own" ? response : responseOpponents;
  }, [response, responseOpponents, typeShots]);

  const currentDisplayedMarkerColorMap = useMemo(() => {
    return typeShots === "own" ? ownMarkerColorMap : opponentMarkerColorMap;
  }, [typeShots, ownMarkerColorMap, opponentMarkerColorMap]);

  const currentDisplayedMergeMarkers = useMemo(() => {
    return typeShots === "own" ? allOwnMarkers : allOpponentsMarkers;
  }, [allOwnMarkers, allOpponentsMarkers, typeShots]);

  const limitExceededCount =
    typeShots === "own"
      ? limitExceededBySide?.own
      : limitExceededBySide?.opponent;

  const timelineScopedMergeMarkers = useMemo(() => {
    const sourceMergeMarkers = currentDisplayedResponseData?.allMarkers || [];
    if (sourceMergeMarkers.length === 0) {
      return [];
    }

    const displayedEventIds = new Set(
      (displayedEvents || []).map((item) => String(item?.id))
    );
    if (displayedEventIds.size === 0) {
      return [];
    }

    return sourceMergeMarkers.filter((markerId) =>
      displayedEventIds.has(String(markerId))
    );
  }, [currentDisplayedResponseData, displayedEvents]);

  const { getDebouncedTimelineHandler } = useTimelineFilter({ debounceMs: 500 });

  useEffect(() => {
    dataForTimeLineChange.current = currentDisplayedResponseData;
    setDisplayedEvents(currentDisplayedResponseData?.events ?? []);
  }, [currentDisplayedResponseData]);

  const debouncedChangeTimeLineRange = useMemo(
    () =>
      getDebouncedTimelineHandler({
        getEvents: () => dataForTimeLineChange.current?.events || [],
        setDisplayedEvents,
      }),
    [getDebouncedTimelineHandler]
  );

  useEffect(() => {
    return () => {
      debouncedChangeTimeLineRange?.cancel?.();
    };
  }, [debouncedChangeTimeLineRange]);

  const fieldWidth = 649;
  const fieldHeight = Math.round((fieldWidth * 68) / 105);
  const fieldPadding = 5;
  const summaryFieldWidth = 649;
  const summaryFieldHeight = 482;

  const convertCoordinatesWithPadding = useCallback(
    (x, y) => {
      const scaleX = (fieldWidth - 2 * fieldPadding) / 104;
      const scaleY = (fieldHeight - 2 * fieldPadding) / 70;
      return {
        x: fieldPadding + x * scaleX,
        y: fieldHeight - fieldPadding - y * scaleY,
      };
    },
    [fieldHeight, fieldPadding, fieldWidth]
  );

  const convertSummaryCoordinatesWithPadding = useCallback(
    (coord_x, coord_y) =>
      convertHalfPitchCoordinates({
        coordX: coord_x,
        coordY: coord_y,
        fieldWidth: summaryFieldWidth,
        fieldHeight: summaryFieldHeight,
      }),
    [summaryFieldHeight, summaryFieldWidth]
  );

  const counts = useMemo(
    () =>
      countHalfPitchZones({
        events: displayedEvents,
        convertCoordinatesWithPadding: convertSummaryCoordinatesWithPadding,
        mirrorCoordinates,
      }),
    [convertSummaryCoordinatesWithPadding, displayedEvents]
  );

  const zonePercentages = useMemo(
    () => calculateZonePercentages(counts),
    [counts]
  );

  const getAllZones = useCallback(
    (count) => {
      const currentStep = count > 14 ? count / 3 : count;
      if ([12, 14].includes(count)) {
        return getCustomZones({
          width: fieldWidth,
          height: fieldHeight,
          quantity: count,
          step: currentStep,
        });
      }
      return getZones({
        width: fieldWidth,
        height: fieldHeight,
        quantity: count,
        step: currentStep,
      });
    },
    [fieldHeight, fieldWidth]
  );

  const {
    hoveredEvent,
    hoveredEventColor,
    hoverCursor,
    handleMarkerHoverStart,
    handleMarkerHoverMove,
    clearMarkerHover,
    resetHover,
  } = useEventMarkerHover();

  const {
    pitchRef,
    drawing,
    toggleDrawingMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    selectedZones,
    handleZoneSelection,
    drawMarkers,
    zoneMarkers,
    eventsCount,
    renderZones,
    renderDrawnShapes,
    renderPreviewShape,
    isDotInsideDrawnShapes,
    resetDrawingZones,
  } = usePitchDrawingZones({
    displayedEvents,
    convertCoordinatesWithPadding,
    getAllZones,
  });

  const resetAllSettings = () => {
    setDisplayedEvents([]);
    resetHover();
    setShowDirection(false);
    setZoneOpen(false);
    setTypeShots("own");
    setFilteredMatchIds(selectedMatchIds);
    setOpenMatchesSelect(false);
    setBodyPartIds([]);
    setFlankIds([]);
    setSequenceContextFilterIds([]);
    resetDrawingZones();
    resetActionSelection();
    resetData();
  };

  const PopoverContent = useMemo(() => {
    if (!groupedSelectedActions) {
      return null;
    }

    return (
      <SelectedActionsPopoverContent
        groupedSelectedActions={groupedSelectedActions}
        l={l}
      />
    );
  }, [groupedSelectedActions, l]);

  const attackTypeLabel = typeShots === "own" ? l(9) : l(1107);
  const selectedParameterLabels = useMemo(
    () => (checkedAction || []).map((action) => l(action.lexic_id) || action.title),
    [checkedAction, l]
  );
  const isBodyPartFilterEnabled = useMemo(
    () =>
      (checkedAction || []).some((action) =>
        SHOT_OR_GOAL_ACTION_RE.test(String(action?.title || ""))
      ),
    [checkedAction]
  );
  useEffect(() => {
    if (!isBodyPartFilterEnabled && bodyPartIds.length > 0) {
      setBodyPartIds([]);
    }
  }, [bodyPartIds.length, isBodyPartFilterEnabled]);
  const parameterSummaryLabel = useMemo(() => {
    if (selectedParameterLabels.length === 0) {
      return "Choose parameters";
    }
    if (selectedParameterLabels.length === 1) {
      return selectedParameterLabels[0];
    }
    return `${selectedParameterLabels[0]} + ${selectedParameterLabels.length - 1}`;
  }, [selectedParameterLabels]);
  const selectedBodyPartLabels = useMemo(
    () =>
      BODY_PART_OPTIONS.filter((option) => bodyPartIds.includes(option.id)).map(
        (option) => option.label
      ),
    [bodyPartIds]
  );
  const selectedFlankLabels = useMemo(
    () =>
      ATTACK_FLANK_OPTIONS.filter((option) => flankIds.includes(option.id)).map(
        (option) => option.label
      ),
    [flankIds]
  );

  const handleEventMarkerClick = useCallback((event) => {
    openVideoList(
      event.id,
      `${event.action} - ${event?.creator?.display_name}`,
      false,
      currentDisplayedMergeMarkers,
      false,
      false,
      sequenceContextFilterIds.length > 0
    );
  }, [currentDisplayedMergeMarkers, sequenceContextFilterIds.length]);

  const watchMarkerIds = useMemo(() => {
    if (drawMarkers?.length > 0) return drawMarkers;
    if (selectedZones.length > 0) return zoneMarkers || [];
    return (displayedEvents || [])
      .filter((event) => event.coord_x != null && event.coord_y != null)
      .map((event) => event.id);
  }, [displayedEvents, drawMarkers, selectedZones.length, zoneMarkers]);
  const watchCount = drawMarkers?.length > 0 ? drawMarkers.length : eventsCount;
  const sequenceWatchCount = useMemo(() => {
    const watchedMarkerIds = new Set(
      watchMarkerIds.map((marker) => String(marker?.id ?? marker))
    );
    const sequenceKeys = new Set();

    (displayedEvents || []).forEach((event) => {
      if (!watchedMarkerIds.has(String(event?.id))) return;
      sequenceKeys.add(
        String(
          event?.possession_sequence_id ??
            event?.details?.possession_sequence_id ??
            `event-${event?.id}`
        )
      );
    });

    return sequenceKeys.size;
  }, [displayedEvents, watchMarkerIds]);
  const isSequenceContextActive = sequenceContextFilterIds.length > 0;
  const watchButtonCount = isSequenceContextActive ? sequenceWatchCount : watchCount;
  const watchButtonTitle = `Watch ${watchButtonCount} ${
    isSequenceContextActive ? "sequences" : "events"
  }`;

  return (
    <>
      <EventsMatchesWrap>
        <ListOfMatches>
          <EventCardWrapper data-tour="matches">
            <h3>{l(995)}</h3>
            <EventCard>
              <EventContent>
                <DateRangeSlider
                  matches={selectedMatches}
                  selectedMatchIds={filteredMatchIds}
                  onRangeChange={handleDateRangeChange}
                />

                <Dropdown
                  ref={matchesListDropdownRef}
                  disabled={(selectedMatches || []).length === 0}
                >
                  <DropdownButton
                    onClick={() => setOpenMatchesSelect((prev) => !prev)}
                  >
                    {selectedMatchesSummaryLabel}
                    <img
                      src={openMatchesSelect ? arrowIconUp : arrowIconDown}
                      alt="arrow"
                    />
                  </DropdownButton>
                  <DropdownContent $show={openMatchesSelect}>
                    {(selectedMatches || []).length > 0 ? (
                      <>
                        <DropdownItem
                          selected={false}
                          onClick={handleDeselectAllMatches}
                          style={{
                            fontFamily: "SatoshiMedium",
                            opacity: hasFilteredMatchSelection ? 1 : 0.6,
                            borderBottom: "1px solid rgba(115, 128, 137, 0.25)",
                          }}
                        >
                          {l(1070)}
                        </DropdownItem>
                        {selectedMatches.map((match) => (
                          <MatchDropdownItem
                            key={match?.id}
                            selected={filteredMatchIdSet.has(String(match?.id))}
                            onClick={() => handleChangeMatches(match)}
                          >
                            <MatchCheckbox
                              type="checkbox"
                              readOnly
                              checked={filteredMatchIdSet.has(String(match?.id))}
                            />
                            {getMatchDisplayLabel(match)}
                          </MatchDropdownItem>
                        ))}
                      </>
                    ) : (
                      <DropdownItem>{l(464)}</DropdownItem>
                    )}
                  </DropdownContent>
                </Dropdown>
              </EventContent>
            </EventCard>
          </EventCardWrapper>
          <EventCardWrapper data-tour="parameters">
            <h3>
              {l(996)}
              {groupedSelectedActions ? (
                <ChoosenSelectionsPopover content={PopoverContent}>
                  Show selections
                </ChoosenSelectionsPopover>
              ) : null}
            </h3>
            <EventCard>
              <button
                type="button"
                onClick={() => setIsParametersOpen((prev) => !prev)}
                aria-expanded={isParametersOpen}
                aria-label={l(996)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "16px 20px",
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    minWidth: 0,
                    color: "rgba(207, 218, 230, 1)",
                    fontFamily: "SatoshiRegular",
                    fontSize: 14,
                    lineHeight: "20px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {parameterSummaryLabel}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    color: _isLightTheme ? "#2363F7" : "#BFE82D",
                    fontSize: 14,
                    lineHeight: 1,
                    transform: isParametersOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                >
                  ▾
                </span>
              </button>
              <div
                aria-hidden={!isParametersOpen}
                style={{
                  maxHeight: isParametersOpen ? 460 : 0,
                  opacity: isParametersOpen ? 1 : 0,
                  overflow: "hidden",
                  transition:
                    "max-height 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease",
                  pointerEvents: isParametersOpen ? "auto" : "none",
                }}
              >
                <EventTabsCard $nested={true}>
              <TabsWrapper
                selectedIndex={paramTabIndex}
                onSelect={setParamTabIndex}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  flex: 1,
                }}
              >
                <ParameterSearchInput
                  ref={searchInputRef}
                  type="text"
                  value={paramSearchQuery}
                  onChange={(e) => handleParamSearchChange(e.target.value)}
                  placeholder={l(906) || "Search"}
                />
                <EventTabsHeader>
                  <TopList ref={tabListRef} $needBackGroundColor={true}>
                    <TabListWrapper
                      style={{
                        overflow: "auto",
                        textWrap: "nowrap",
                        scrollbarWidth: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      {paramsList?.map((item) => {
                        const countSelected =
                          groupedSelectedActions?.[item.lexic_id]?.length || 0;
                        return (
                          <TabTitle key={item.id}>
                            {l(item.lexic_id) || item.title}
                            {countSelected > 0 && <span>{countSelected}</span>}
                          </TabTitle>
                        );
                      })}
                    </TabListWrapper>
                  </TopList>
                </EventTabsHeader>
                <div
                  style={{
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <EventTabsScrollArea ref={parameterScrollRef}>
                    {paramsList?.map((item) => {
                      return (
                        <TabContent key={item.id}>
                          <ParametersTabWrapper>
                            {item?.actions?.map((action) => {
                              const isHighlighted =
                                action.id === highlightedActionId;
                              return (
                                <div
                                  className={`checkboxField${
                                    isHighlighted ? " is-highlighted" : ""
                                  }`}
                                  key={action.id}
                                  ref={isHighlighted ? highlightedRef : null}
                                >
                                  <label htmlFor={action.id}>
                                    <input
                                      id={action.id}
                                      type="checkbox"
                                      checked={checkedIds.includes(action.id)}
                                      onChange={() =>
                                        handleCheckboxChange(action, item)
                                      }
                                    />
                                    {l(action.lexic_id) || action.title}
                                  </label>
                                </div>
                              );
                            })}
                          </ParametersTabWrapper>
                        </TabContent>
                      );
                    })}
                  </EventTabsScrollArea>
                  {showParameterScrollIndicator ? (
                    <div
                      style={{
                        position: "absolute",
                        left: 20,
                        right: 20,
                        bottom: 0,
                        height: 42,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        paddingBottom: 8,
                        pointerEvents: "none",
                        background:
                          "linear-gradient(to bottom, rgba(0, 0, 0, 0), var(--events-scroll-indicator-bg, rgba(0, 0, 0, 0.85)))",
                        color: _isLightTheme ? "#2363F7" : "#BFE82D",
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      ↓
                    </div>
                  ) : null}
                </div>
              </TabsWrapper>
                </EventTabsCard>
              </div>
            </EventCard>
          </EventCardWrapper>
          {hasEndingSequenceContext ? (
            <EndingCreatorToggle htmlFor="player-ending-context-same-creator">
              Player is creator of both actions
              <EndingCreatorToggleInput
                id="player-ending-context-same-creator"
                type="checkbox"
                checked={requirePlayerAsEndingCreator}
                onChange={() => setRequirePlayerAsEndingCreator((current) => !current)}
              />
              <EndingCreatorToggleTrack />
            </EndingCreatorToggle>
          ) : null}
          <SequenceContextFilterPanel
            scope="player"
            selectedIds={sequenceContextFilterIds}
            onToggle={toggleSequenceContextFilter}
            onClear={clearSequenceContextFilters}
            tourId="sequence-context"
          />
          <CollapsibleFilterSection
            title="Flow Flanks"
            emptySummary="Choose flow flank"
            selectedLabels={selectedFlankLabels}
            tourId="flanks"
          >
            <ParametersTabWrapper>
              <div className="checkboxField">
                <label htmlFor="events-flank-left">
                  <input
                    id="events-flank-left"
                    type="checkbox"
                    checked={flankIds.includes("left")}
                    onChange={() => toggleFlank("left")}
                  />
                  Left
                </label>
              </div>
              <div className="checkboxField">
                <label htmlFor="events-flank-center">
                  <input
                    id="events-flank-center"
                    type="checkbox"
                    checked={flankIds.includes("center")}
                    onChange={() => toggleFlank("center")}
                  />
                  Center
                </label>
              </div>
              <div className="checkboxField">
                <label htmlFor="events-flank-right">
                  <input
                    id="events-flank-right"
                    type="checkbox"
                    checked={flankIds.includes("right")}
                    onChange={() => toggleFlank("right")}
                  />
                  Right
                </label>
              </div>
            </ParametersTabWrapper>
          </CollapsibleFilterSection>
          <CollapsibleFilterSection
            title={l(1617)}
            emptySummary="Choose body part"
            selectedLabels={selectedBodyPartLabels}
            tourId="body-part"
            disabled={!isBodyPartFilterEnabled}
            disabledTooltip={BODY_PART_DISABLED_TOOLTIP}
          >
            <ParametersTabWrapper>
              <div className="checkboxField">
                <label htmlFor="events-player-body-part-all">
                  <input
                    id="events-player-body-part-all"
                    type="checkbox"
                    checked={bodyPartIds.length === 0}
                    onChange={() => setBodyPartIds([])}
                  />
                  {l(1618)}
                </label>
              </div>
              <div className="checkboxField">
                <label htmlFor="events-player-body-part-left">
                  <input
                    id="events-player-body-part-left"
                    type="checkbox"
                    checked={bodyPartIds.includes(2)}
                    onChange={() => toggleBodyPart(2)}
                  />
                  {l(1613)}
                </label>
              </div>
              <div className="checkboxField">
                <label htmlFor="events-player-body-part-right">
                  <input
                    id="events-player-body-part-right"
                    type="checkbox"
                    checked={bodyPartIds.includes(1)}
                    onChange={() => toggleBodyPart(1)}
                  />
                  {l(1614)}
                </label>
              </div>
              <div className="checkboxField">
                <label htmlFor="events-player-body-part-header">
                  <input
                    id="events-player-body-part-header"
                    type="checkbox"
                    checked={bodyPartIds.includes(3)}
                    onChange={() => toggleBodyPart(3)}
                  />
                  {l(1615)}
                </label>
              </div>
              <div className="checkboxField">
                <label htmlFor="events-player-body-part-body">
                  <input
                    id="events-player-body-part-body"
                    type="checkbox"
                    checked={bodyPartIds.includes(4)}
                    onChange={() => toggleBodyPart(4)}
                  />
                  {l(1616)}
                </label>
              </div>
            </ParametersTabWrapper>
          </CollapsibleFilterSection>
        </ListOfMatches>

        <FootballField
          onMouseDown={drawing ? handleMouseDown : null}
          onMouseMove={drawing ? handleMouseMove : null}
          onMouseUp={drawing ? handleMouseUp : null}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              onClick={resetAllSettings}
              title={l(457)}
              size={"normal"}
              style={{ margin: "0 20px 20px 5px" }}
            />

            <Button
              onClick={toggleDrawingMode}
              title={drawing ? l(1074) : l(997)}
              size={"normal"}
              style={{ margin: "0 5px 20px 20px" }}
            />
          </div>

          <FootballFielOptions>
            <VideoSettingsComponent
              showDirection={showDirection}
              setShowDirection={setShowDirection}
              selectedZones={selectedZones}
              handleZoneSelection={handleZoneSelection}
              zoneOpen={zoneOpen}
              setZoneOpen={setZoneOpen}
              user={user}
            />
            <span data-tour="watch" style={{ display: "inline-flex" }}>
            <WatchEventsButton
              count={watchButtonCount}
              title={watchButtonTitle}
              onClick={() => {
                if (drawMarkers?.length > 0) {
                  openVideoList(
                    drawMarkers.map((item) => item)?.join(","),
                    `${attackTypeLabel} - ${players?.player?.display_name}`,
                    false,
                    [],
                    false,
                    false,
                    sequenceContextFilterIds.length > 0
                  );
                } else {
                  const markersToShow =
                    selectedZones.length > 0
                      ? zoneMarkers?.map((item) => item)?.join(",")
                      : displayedEvents?.filter((e) => e.coord_x != null && e.coord_y != null)?.map((item) => item.id)?.join(",");
                  openVideoList(
                    markersToShow,
                    `${attackTypeLabel} - ${players?.player?.display_name}`,
                    false,
                    selectedZones.length === 0 ? timelineScopedMergeMarkers : [],
                    false,
                    false,
                    sequenceContextFilterIds.length > 0
                  );
                }
              }}
              onContextMenu={() => {
                if (drawMarkers?.length > 0) {
                  openVideoList(
                    drawMarkers.map((item) => item)?.join(","),
                    `${attackTypeLabel} - ${players?.player?.display_name}`,
                    false,
                    [],
                    false,
                    false,
                    sequenceContextFilterIds.length > 0
                  );
                } else {
                  const markersToShow =
                    selectedZones.length > 0
                      ? zoneMarkers?.map((item) => item)?.join(",")
                      : displayedEvents?.filter((e) => e.coord_x != null && e.coord_y != null)?.map((item) => item.id)?.join(",");
                  openVideoList(
                    markersToShow,
                    `${attackTypeLabel} - ${players?.player?.display_name}`,
                    false,
                    selectedZones.length === 0 ? timelineScopedMergeMarkers : [],
                    false,
                    false,
                    sequenceContextFilterIds.length > 0
                  );
                }
              }}
            />
            </span>
          </FootballFielOptions>

          {limitExceededCount ? (
            <div
              role="alert"
              style={{
                margin: "0 5px 16px",
                padding: "14px 18px",
                borderRadius: 12,
                border: `1px solid ${_isLightTheme ? "#F2A20A" : "#BFE82D"}`,
                background: _isLightTheme
                  ? "rgba(242, 162, 10, 0.12)"
                  : "rgba(191, 232, 45, 0.1)",
                color: _isLightTheme ? "#7A5200" : "#BFE82D",
                fontFamily: "SatoshiMedium",
                fontSize: 14,
                lineHeight: "20px",
              }}
            >
              {buildEventsResultLimitMessage(limitExceededCount)}
            </div>
          ) : null}

          <PitchLoadingOverlay loading={loadingMarkers}>
            <PitchTrackerFrame data-tour="pitch">
              <PitchTrackerSvg
                ref={pitchRef}
                width={fieldWidth + 2 * fieldPadding}
                height={fieldHeight + 2 * fieldPadding}
                viewBox={`0 0 ${fieldWidth + 2 * fieldPadding} ${
                  fieldHeight + 2 * fieldPadding
                }`}
                preserveAspectRatio="xMidYMid meet"
                $drawing={drawing}
              >
                <EventPitchBaseLayer
                  fieldWidth={fieldWidth}
                  fieldHeight={fieldHeight}
                  fieldPadding={fieldPadding}
                />
                {renderZones()}
                {renderDrawnShapes()}
                {renderPreviewShape()}

                {displayedEvents?.map((event) => {
                  const {
                    coord_x,
                    coord_y,
                    coord_x_destination,
                    coord_y_destination,
                  } = event;
                  const position = convertCoordinatesWithPadding(coord_x, coord_y);
                  const destinationPosition =
                    coord_x_destination != null && coord_y_destination != null
                      ? convertCoordinatesWithPadding(
                          coord_x_destination,
                          coord_y_destination
                        )
                      : null;
                  const markerColor = getMarkerEventColor(
                    event.id,
                    currentDisplayedMarkerColorMap
                  );
                  const isSelectedByZone = isDotInsideDrawnShapes(event);
                  const isHovered = hoveredEvent?.id === event.id;

                  return (
                    <React.Fragment key={event.id}>
                      {showDirection && destinationPosition && (
                        <line
                          x1={position.x}
                          y1={position.y}
                          x2={destinationPosition.x}
                          y2={destinationPosition.y}
                          stroke={_isLightTheme ? "#2363F7" : "#BFE82D"}
                          strokeWidth="2"
                          strokeDasharray="0"
                        />
                      )}
                      <g
                        role="button"
                        tabIndex={0}
                        className={`event-dot-group ${isHovered ? "is-hovered" : ""}`}
                        onClick={() => handleEventMarkerClick(event)}
                        onKeyDown={(nativeEvent) => {
                          if (nativeEvent.key === "Enter" || nativeEvent.key === " ") {
                            nativeEvent.preventDefault();
                            handleEventMarkerClick(event);
                          }
                        }}
                        onMouseEnter={(nativeEvent) =>
                          handleMarkerHoverStart(event, markerColor, nativeEvent)
                        }
                        onMouseMove={handleMarkerHoverMove}
                        onMouseLeave={clearMarkerHover}
                      >
                        <circle
                          className="event-dot-hover-ring"
                          cx={position.x}
                          cy={position.y}
                          r="9.6"
                          stroke={markerColor}
                        />
                        <circle
                          className="event-dot-core"
                          cx={position.x}
                          cy={position.y}
                          r="5"
                          fill={markerColor}
                        />
                      </g>
                      {isSelectedByZone && (
                        <circle
                          cx={position.x}
                          cy={position.y}
                          r={6.2}
                          fill="none"
                          stroke="#F20A46"
                          strokeWidth={2}
                          pointerEvents="none"
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </PitchTrackerSvg>
            </PitchTrackerFrame>
          </PitchLoadingOverlay>

          <EventHoverTracker
            event={hoveredEvent}
            visible={!!hoveredEvent}
            badgeColor={hoveredEventColor}
            cursorX={hoverCursor.x}
            cursorY={hoverCursor.y}
            emptyText={l(464)}
          />

          <div data-tour="time-range">
            <TimelineChange
              onChange={debouncedChangeTimeLineRange}
              min={timeLineRange?.min}
              max={timeLineRange?.max}
              styleContainer={{ marginTop: 35, marginBottom: 20 }}
            />
          </div>

          <FootballFieldCount>
            <EventHalfPitchStaticPreview
              fieldWidth={summaryFieldWidth}
              fieldHeight={summaryFieldHeight}
              fieldPadding={0}
            />

            <FieldCount>
              <TopFields>
                <LeftTopField>
                  {displayedEvents?.length > 0
                    ? `${zonePercentages.leftTop} %`
                    : "0"}
                </LeftTopField>
                <MiddleTopField>
                  {displayedEvents?.length > 0
                    ? `${zonePercentages.middleTop} %`
                    : "0"}
                </MiddleTopField>
                <RightTopField>
                  {displayedEvents?.length > 0
                    ? `${zonePercentages.rightTop} %`
                    : "0"}
                </RightTopField>
              </TopFields>
              <BottomFields>
                {displayedEvents?.length > 0
                  ? `${zonePercentages.bottom} %`
                  : "0"}
              </BottomFields>
            </FieldCount>
          </FootballFieldCount>
        </FootballField>
      </EventsMatchesWrap>
      <PageScrollDownButton />
      <EventsTutorial enabled={isActive} />
    </>
  );
};

export default EventsPlayer;
